import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import { usePostAuthApi } from '@/hooks/UseApiCalls';
/** yyyy-MM-dd from the picker to dd/MM/yyyy for Nedarim, without going through a Date. */
function toNedarimDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
}
import { translateNedarimError } from '../NewStandingOrder/useNedarimIframe';
import {
  formatMoney, getCurrencyCode, CURRENCY_BY_CODE, formatCardExpiry, getCardExpiryState,
} from '../standingOrderModel';
import styles from './StandingOrderChargeDialog.module.css';

interface CreditChargeRequest {
  amount: number;
  currency: number;
  comments?: string;
}

interface BankChargeRequest {
  amount: number;
  date: string;
}

interface ChargeResponse {
  success: boolean;
  message?: string;
}

interface StandingOrderChargeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: 'credit' | 'bank';
  fundId: number;
  standingOrderId: number;
  mosadSource: string;
  defaultAmount: number;
  currency: string | null;
  /** Who is about to be charged and off what, so the dialog is not a form with no subject. */
  donorName?: string | null;
  paymentSource?: string | null;
  /** The card's expiry, so a charge against a dying card is not made blind. */
  validity?: string | null;
  onChargeSucceeded?: () => void;
}

/** Local yyyy-MM-dd: toISOString() here would offer yesterday as the earliest date. */
function todayIso(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

const CURRENCY_SYMBOL: Record<1 | 2, string> = { 1: '₪', 2: '$' };

const StandingOrderChargeDialog = ({
  open,
  onOpenChange,
  type,
  fundId,
  standingOrderId,
  mosadSource,
  defaultAmount,
  currency,
  donorName,
  paymentSource,
  validity,
  onChargeSucceeded,
}: StandingOrderChargeDialogProps) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const isCredit = type === 'credit';
  const cardExpiryState = getCardExpiryState(validity ?? null);
  const i18nPrefix = `standingOrders.${type}StandingOrderCharge`;

  // A currency we cannot read is not assumed to be shekels: charging a dollar standing order
  // in shekels takes the wrong amount from the donor and nothing on screen would say so.
  const currencyCode = isCredit ? getCurrencyCode(currency) : 1;
  const currencySymbol = currencyCode == null ? '' : CURRENCY_SYMBOL[currencyCode];

  const [amount, setAmount] = useState(String(defaultAmount));
  const [comments, setComments] = useState('');
  const [date, setDate] = useState('');
  const [warningAcknowledged, setWarningAcknowledged] = useState(false);
  const [resultMessage, setResultMessage] = useState<{ success: boolean; text: string } | null>(null);

  const chargeUrl = `nedarim-standing-orders/${fundId}/${type}/${standingOrderId}/charge?mosadSource=${mosadSource}`;
  const { mutate: charge, isPending } = usePostAuthApi<ChargeResponse, CreditChargeRequest | BankChargeRequest>(chargeUrl, {
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: [`nedarim-standing-orders/${fundId}`] });
        queryClient.invalidateQueries({
          queryKey: [`nedarim-standing-orders/${fundId}/${type}/${standingOrderId}/details?mosadSource=${mosadSource}`],
        });
        // The dialog used to close in silence, so nobody could tell a charge that went through
        // from one that never left the screen. The success wording already existed, unused.
        setResultMessage({ success: true, text: t(`${i18nPrefix}.success`) });
        onChargeSucceeded?.();
      } else {
        setResultMessage({ success: false, text: data.message ? translateNedarimError(data.message, t) : t(`${i18nPrefix}.error`) });
      }
    },
    onError: () => {
      setResultMessage({ success: false, text: t(`${i18nPrefix}.error`) });
    },
  });

  const handleSubmit = () => {
    if (currencyCode == null) {
      setResultMessage({ success: false, text: t('standingOrders.unknownCurrency') });
      return;
    }

    const parsedAmount = parseFloat(amount);
    if (!(parsedAmount > 0)) {
      setResultMessage({ success: false, text: t(`${i18nPrefix}.amountRequired`) });
      return;
    }

    if (!isCredit && !date) {
      setResultMessage({ success: false, text: t(`${i18nPrefix}.dateRequired`) });
      return;
    }

    setResultMessage(null);

    if (isCredit) {
      const body: CreditChargeRequest = {
        amount: parsedAmount,
        currency: currencyCode,
      };
      if (comments.trim()) body.comments = comments.trim();
      charge(body);
    } else {
      const body: BankChargeRequest = {
        amount: parsedAmount,
        // The picker hands over yyyy-MM-dd. new Date() on that is midnight UTC, which is the
        // previous evening here, so the masav run was booked for the day before the one chosen.
        date: toNedarimDate(date),
      };
      charge(body);
    }
  };

  const handleClose = (value: boolean) => {
    if (!isPending) {
      onOpenChange(value);
      if (!value) {
        setAmount(String(defaultAmount));
        setComments('');
        setDate('');
        setWarningAcknowledged(false);
        setResultMessage(null);
      }
    }
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={handleClose}
      title={t(`${i18nPrefix}.title`)}
      maxWidth="640px"
      fitContent
      /* Two fields and a button: the shared layout's title and divider spacing, sized for a
         multi-step form, left this dialog half empty above the fold. */
      contentClassName={styles.compactDialog}
    >
      <div className={styles.container}>
        <div className={styles.form}>
          {(donorName || paymentSource) && (
            <div className={styles.context}>
              <span>{[donorName, paymentSource].filter(Boolean).join(' · ')}</span>
              <b>
                {t(`${i18nPrefix}.standingAmount`, {
                  amount: formatMoney(
                    defaultAmount,
                    currencyCode == null ? 'ILS' : CURRENCY_BY_CODE[currencyCode],
                    i18n.language,
                  ),
                })}
              </b>
            </div>
          )}
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="charge-amount">
                {t(`${i18nPrefix}.amount`)}
                <span className={styles.required}>*</span>
              </label>
              <div className={styles.inputWrapper}>
                <span className={styles.currencyBadge}>{currencySymbol}</span>
                <input
                  id="charge-amount"
                  className={styles.input}
                  type="number"
                  min="1"
                  step="any"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  disabled={isPending}
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                />
              </div>
            </div>
            {!isCredit && (
              <div className={styles.field}>
                <label className={styles.label} htmlFor="charge-date">
                  {t(`${i18nPrefix}.date`)}
                  <span className={styles.required}>*</span>
                </label>
                <input
                  id="charge-date"
                  className={styles.input}
                  type="date"
                  min={todayIso()}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  disabled={isPending}
                />
              </div>
            )}
            {/* Beside the amount, as the proposal draws it, rather than a second full-width row
                that pushed the amount away from the button that confirms it. */}
            {isCredit && (
              <div className={styles.field}>
                <label className={styles.label} htmlFor="charge-comments">
                  {t(`${i18nPrefix}.comments`)}
                </label>
                <input
                  id="charge-comments"
                  className={styles.input}
                  type="text"
                  value={comments}
                  placeholder={t(`${i18nPrefix}.commentsPlaceholder`)}
                  onChange={(e) => setComments(e.target.value)}
                  disabled={isPending}
                />
              </div>
            )}
          </div>

          {!isCredit && (
            <div className={styles.bankWarning}>
              <div className={styles.bankWarningIcon}>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
                  <path
                    d="M20.39 21.1H4.2c-1.55 0-2.52-1.68-1.75-3.02l8.1-14.08a2 2 0 0 1 3.49 0l8.1 14.08c.77 1.34-.2 3.02-1.75 3.02Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path d="M12.29 9.5V13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <circle cx="12.29" cy="17.1" r="1" fill="currentColor" />
                </svg>
              </div>
              <p className={styles.bankWarningText}>{t(`${i18nPrefix}.warning`)}</p>
              <label className={styles.bankWarningCheckbox}>
                <input
                  type="checkbox"
                  checked={warningAcknowledged}
                  onChange={(e) => setWarningAcknowledged(e.target.checked)}
                  disabled={isPending}
                />
                <span>{t(`${i18nPrefix}.warningAcknowledge`)}</span>
              </label>
            </div>
          )}

          {resultMessage && (
            <div
              role={resultMessage.success ? 'status' : 'alert'}
              className={resultMessage.success ? styles.successMessage : styles.errorMessage}
            >
              {resultMessage.text}
            </div>
          )}

          {/*
            * An expiring card is the reason most of these retries were needed in the first
            * place, and the retry will fail again for the same reason. Saying so here, with
            * the way out of it, saves a second failed charge against the donor's card.
            */}
          {isCredit && cardExpiryState !== 'valid' && (
            <div className={styles.expiryNotice} role="status">
              {t(cardExpiryState === 'expired'
                ? `${i18nPrefix}.expiryWarning`
                : `${i18nPrefix}.expiringWarning`, { expiry: formatCardExpiry(validity ?? null) })}
            </div>
          )}

          <div className={styles.actions}>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={
                isPending || currencyCode == null || !(parseFloat(amount) > 0)
                || (!isCredit && !warningAcknowledged)
              }
            >
              {isPending
                ? t(`${i18nPrefix}.charging`)
                /*
                 * The figure goes on the button. Taking money from a donor is the one action
                 * on this screen that cannot be undone, and "confirm" on its own does not say
                 * how much - the amount is editable, so it is not necessarily the one above.
                 */
                : parseFloat(amount) > 0
                  ? t(`${i18nPrefix}.confirmAmount`, {
                    amount: formatMoney(
                      parseFloat(amount),
                      currencyCode == null ? 'ILS' : CURRENCY_BY_CODE[currencyCode],
                      i18n.language,
                    ),
                  })
                  : t(`${i18nPrefix}.confirm`)}
            </Button>
            <Button
              variant="outline"
              /* The variant pins !h-9 and !min-w-0; this dialog's buttons are the larger
                 pair the proposal draws, so the override is important too. */
              className="!h-[52px] !min-w-[120px] !px-6"
              onClick={() => handleClose(false)}
              disabled={isPending}
            >
              {t(`${i18nPrefix}.cancel`)}
            </Button>
          </div>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default StandingOrderChargeDialog;
